import { twClassMerge } from '@healthhub/react-ui';
import type { FormInstance, UploadProps } from 'antd';
import { Typography, Upload } from 'antd';
import type { RcFile, UploadChangeParam } from 'antd/es/upload';
import type { KeyboardEvent, ReactElement, SyntheticEvent } from 'react';
import { memo, useState } from 'react';

import { CommonProfile } from '@shared/assets';
import { FilledCameraIcon } from '@shared/assets-icon';
import type { ResizeFilePayload } from '@shared/utils/image';
import { resizeFile } from '@shared/utils/image';

import {
  BYTES_IN_GB,
  BYTES_IN_KB,
  BYTES_IN_MB,
  DEFAULT_MAX_FILE_SIZE,
  ONE_MB_TO_BYTES,
  VALID_IMG_TYPES,
} from './constants';
import { ConfirmRemovePhotoModal, ExceedLimitModal, InvalidTypeModal, OptionsModal } from './modals';

const { Text } = Typography;

interface Props extends UploadProps {
  children?: ReactElement;
  defaultImage?: null | string;
  disabled?: boolean;
  fallBackImage?: string;
  form: FormInstance;
  isShowCameraIcon?: boolean;
  isShowList?: boolean;
  limitFileSize?: number;
  placeholderImg?: string;
  resize?: boolean;
  resizePayload?: ResizeFilePayload;
  showExceedLimitErrorMsg?: boolean;
  showUploadList?: boolean;
}

const CustomUploadImage = ({
  accept = 'image/*',
  children,
  className,
  defaultImage,
  disabled = false,
  fallBackImage,
  form,
  isShowCameraIcon,
  isShowList = false,
  limitFileSize = DEFAULT_MAX_FILE_SIZE,
  listType = 'picture',
  maxCount = 1,
  multiple = false,
  name = 'Photo',
  placeholderImg = CommonProfile,
  resize,
  resizePayload,
  showExceedLimitErrorMsg = false,
  ...restProps
}: Props) => {
  const idUpload = 'upload-element';
  const [isExceedLimitSize, setIsExceedLimitSize] = useState<boolean>(false);
  const [initImage, setInitImage] = useState<null | string>(defaultImage ?? null);
  const [currentFile, setCurrentFile] = useState<null | RcFile>(null);
  const [isShowOptionsModal, setIsShowOptionsModal] = useState(false);
  const [isShowRemoveConfirmation, setIsShowRemoveConfirmation] = useState(false);
  const [isShowExceedLimitModal, setIsShowExceedLimitModal] = useState<boolean>(false);
  const [isInvalidType, setIsInvalidType] = useState<boolean>(false);

  const handleImageSource = async (file: File) => {
    if (!!file && Number(file?.size) <= ONE_MB_TO_BYTES) {
      setCurrentFile(file as RcFile);
      return file;
    }

    if (resize && resizePayload) {
      const resizedFileObject = await resizeFile(file, resizePayload);
      setCurrentFile(resizedFileObject as RcFile);
      return resizedFileObject;
    }

    setCurrentFile(file as RcFile);
    return file;
  };

  const handleBeforeUpload = async (file: RcFile) => {
    const { size, type } = file;
    const isValidType = VALID_IMG_TYPES.includes(type);
    const isValidFileSize = size <= limitFileSize;
    if (!isValidType) {
      setIsInvalidType(true);
      return Upload.LIST_IGNORE;
    }

    if (!isValidFileSize) {
      setIsExceedLimitSize(true);
      setIsShowExceedLimitModal(true);
      return Upload.LIST_IGNORE;
    }

    handleImageSource(file);
    return false;
  };

  const handleUploadChange = async (info: UploadChangeParam) => {
    if (info.fileList?.length) {
      const processedFile = await handleImageSource(info.fileList[0].originFileObj as File);
      form.setFieldsValue({
        [name]: processedFile,
      });
    }
  };

  const onClickUpload = () => {
    document.getElementById(idUpload)?.click();
  };

  const handleUploadAction = () => {
    if (currentFile || initImage) {
      setIsShowOptionsModal(true);
    } else {
      onClickUpload();
    }
  };

  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    if (!disabled) {
      handleUploadAction();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
      handleUploadAction();
    }
  };

  const handleCancel = () => {
    setIsShowOptionsModal(false);
  };

  const handleReplacePhoto = () => {
    onClickUpload();
    setIsShowOptionsModal(false);
  };

  const handleRemovePhoto = () => {
    setIsShowOptionsModal(false);
    setIsShowRemoveConfirmation(true);
  };

  const handleConfirmRemove = () => {
    form.setFieldsValue({
      [name]: undefined,
    });
    setCurrentFile(null);
    setInitImage(null);
    setIsShowRemoveConfirmation(false);
  };

  const onCleanUp = () => {
    setIsExceedLimitSize(false);
    setIsInvalidType(false);
    setIsShowExceedLimitModal(false);
  };

  const getFileSizeExceededMessage = (maxFileSizeInByte: number) => {
    if (maxFileSizeInByte < BYTES_IN_KB) {
      return `Image must be smaller than ${BYTES_IN_KB} bytes.`;
    }

    if (maxFileSizeInByte < BYTES_IN_MB) {
      const sizeInKB = maxFileSizeInByte / BYTES_IN_MB;
      return `Image must be smaller than ${sizeInKB.toFixed(2)}KB.`;
    }

    if (maxFileSizeInByte < BYTES_IN_GB) {
      const sizeInMB = maxFileSizeInByte / BYTES_IN_MB;
      return `Image must be smaller than ${sizeInMB.toFixed(2)}MB.`;
    }

    const sizeInGB = maxFileSizeInByte / BYTES_IN_GB;
    return `Image must be smaller than ${sizeInGB.toFixed(2)}GB.`;
  };

  return (
    <>
      <Upload
        accept={accept}
        // action={handleUploadAction}
        beforeUpload={handleBeforeUpload}
        disabled={disabled}
        id={idUpload}
        listType={listType}
        maxCount={maxCount}
        multiple={multiple}
        name={name}
        onChange={handleUploadChange}
        showUploadList={isShowList}
        {...restProps}
      >
        <button
          className={twClassMerge(
            'flex flex-col items-center justify-center text-start shrink-0 w-25.5',
            disabled && 'cursor-not-allowed'
          )}
          disabled={disabled}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          type="button"
        >
          <div className="relative">
            <img
              alt=""
              className={twClassMerge('size-25.5 rounded-3xl object-cover gbomit shrink-0', className)}
              onError={(e) => {
                if (fallBackImage) {
                  e.currentTarget.src = fallBackImage;
                }
              }}
              src={currentFile ? URL.createObjectURL(currentFile) : (initImage ?? placeholderImg)}
            />
            {!!isShowCameraIcon && <FilledCameraIcon className="absolute bottom-0 right-0" />}
          </div>
          {!disabled && (
            <span className={twClassMerge('mt-2.5px text-center text-base font-semibold text-theme-600')}>
              {currentFile || defaultImage ? 'Update photo' : 'Upload photo'}
            </span>
          )}
        </button>
        {isExceedLimitSize && showExceedLimitErrorMsg ? (
          <Text className="mt-[-0.938rem] text-sm text-[#d42024]" type="danger">
            {getFileSizeExceededMessage(limitFileSize)}
          </Text>
        ) : null}
      </Upload>
      <OptionsModal
        handleCancel={handleCancel}
        handleRemovePhoto={handleRemovePhoto}
        handleReplacePhoto={handleReplacePhoto}
        isShowOptionsModal={isShowOptionsModal}
        onCancel={() => setIsShowOptionsModal(false)}
      />
      <ConfirmRemovePhotoModal
        isShowRemoveConfirmation={isShowRemoveConfirmation}
        onCancel={() => setIsShowRemoveConfirmation(false)}
        onOk={handleConfirmRemove}
      />
      <ExceedLimitModal isShowExceedLimitModal={isShowExceedLimitModal} onOk={onCleanUp} />
      <InvalidTypeModal onOk={onCleanUp} open={isInvalidType} />
    </>
  );
};

export default memo(CustomUploadImage);
